import React, { createContext, useContext, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { CheckCircle2 } from "lucide-react";
import { submitLead } from "../lib/leadsApi";

const Ctx = createContext({ openModal: () => {}, closeModal: () => {} });
export const useTestRide = () => useContext(Ctx);

export function TestRideProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [variant, setVariant] = useState("test-ride");
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "Bengaluru", model: "", date: "" });

  const openModal = useCallback((presetData = {}) => {
    setForm((f) => ({
      ...f,
      model: presetData.model || f.model,
      city: presetData.city || f.city,
    }));
    setVariant(presetData.variant || "test-ride");
    setSubmitted(false);
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => setOpen(false), []);

  const handleSubmit = (e) => {
    e.preventDefault();
    submitLead({
      type: variant === "partner" ? "franchise" : "test-ride",
      name: form.name,
      phone: form.phone,
      email: form.email,
      city: form.city,
      model: form.model,
      date: form.date,
    });
    setSubmitted(true);
  };

  const isPartner = variant === "partner";
  const copy = isPartner
    ? {
        eyebrow: "Franchise",
        title: "Become a partner",
        desc: "Share your details and city — our franchise team will reach out on WhatsApp.",
        submit: "Submit enquiry",
        successTitle: "Enquiry received",
        successText: (name, city) =>
          `Thanks ${name || "there"}. Our franchise team will reach out about opening an eBikeGo Store in ${city} within 24 hours.`,
      }
    : {
        eyebrow: "Test ride",
        title: "Book a test ride",
        desc: "Pick your city, model and date — we'll confirm on WhatsApp.",
        submit: "Confirm booking",
        successTitle: "Test ride booked",
        successText: (name, city, phone) =>
          `Thanks ${name || "rider"}. Our ${city} team will confirm your slot on ${phone || "your number"} within 2 hours.`,
      };

  return (
    <Ctx.Provider value={{ openModal, closeModal }}>
      {children}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border border-slate-200 text-slate-900 max-w-lg rounded-2xl p-0 overflow-hidden shadow-2xl"
          data-testid="test-ride-modal"
        >
          <div className="relative">
            <div className="absolute inset-0 helix-bg pointer-events-none opacity-70" />
            <div className="relative p-8">
              {!submitted ? (
                <>
                  <DialogHeader className="text-left mb-6">
                    <p className="eyebrow">{copy.eyebrow}</p>
                    <DialogTitle className="font-outfit text-3xl font-bold mt-3 text-slate-900">
                      {copy.title}
                    </DialogTitle>
                    <DialogDescription className="text-slate-600 mt-2">
                      {copy.desc}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="test-ride-form">
                    <div className="grid grid-cols-2 gap-3">
                      <Input label="Full name" name="name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                      <Input label="Phone" name="phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                    </div>
                    <Input label="Email" name="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" />
                    <div className={isPartner ? "" : "grid grid-cols-2 gap-3"}>
                      <Select
                        label="City"
                        name="city"
                        value={form.city}
                        onChange={(v) => setForm({ ...form, city: v })}
                        options={["Bengaluru", "Mumbai", "Delhi", "Pune", "Hyderabad", "Chennai"]}
                      />
                      {!isPartner && (
                        <Select
                          label="Model"
                          name="model"
                          value={form.model}
                          onChange={(v) => setForm({ ...form, model: v })}
                          options={["Any model", "Urban E-Cycles", "MTB Series", "Foldable", "E-Dirt", "Cargo Utility", "Kids Cycles"]}
                        />
                      )}
                    </div>
                    {!isPartner && (
                      <Input label="Preferred date" name="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} type="date" />
                    )}
                    <button type="submit" className="btn-primary w-full mt-2" data-testid="test-ride-submit">
                      {copy.submit}
                    </button>
                    <p className="text-xs text-slate-500 text-center mt-2">
                      {isPartner ? "No obligation. Our team will share the full FICO model." : "No commitments. Free test ride at your nearest store."}
                    </p>
                  </form>
                </>
              ) : (
                <div className="py-6 text-center" data-testid="test-ride-success">
                  <div className="mx-auto w-16 h-16 rounded-full bg-[#3FE0A6]/15 border border-[#15B47E]/30 inline-flex items-center justify-center mb-5">
                    <CheckCircle2 className="text-[#15B47E]" size={28} />
                  </div>
                  <h3 className="font-outfit text-2xl font-bold mb-2 text-slate-900">{copy.successTitle}</h3>
                  <p className="text-slate-600 text-sm max-w-sm mx-auto">
                    {copy.successText(form.name?.split(" ")[0], form.city, form.phone)}
                  </p>
                  <button onClick={closeModal} className="btn-outline mt-6" data-testid="test-ride-close">
                    Close
                  </button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Ctx.Provider>
  );
}

function Input({ label, value, onChange, name, type = "text" }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`field-${name}`}
        className="mt-1.5 w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-900 text-sm focus:border-[#2A8BFF] focus:ring-2 focus:ring-[#2A8BFF]/20 focus:outline-none transition placeholder:text-slate-400"
      />
    </label>
  );
}

function Select({ label, value, onChange, name, options }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</span>
      <select
        name={name}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`field-${name}`}
        className="mt-1.5 w-full bg-white border border-slate-200 rounded-lg px-4 py-3 text-slate-900 text-sm focus:border-[#2A8BFF] focus:ring-2 focus:ring-[#2A8BFF]/20 focus:outline-none transition"
      >
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}
